import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { createOrg, discoverSignup, createJoinRequest, withdrawJoinRequest, useMyJoinRequests } from '../data/entities';

// Where a verified person with no seat starts. Three branches, decided by
// GET /signup/discover on their email domain (the same answer the server
// enforces in POST /orgs):
//   consumer domain               → create an organization (owner)
//   company domain, nobody proved → create + nudge to verify the domain
//   company domain, proven by X   → "X already uses nventr" → request to
//                                   join (an admin approves); creating a
//                                   separate org only if X allows it.
// Rendered by the Organizations page and by the console gate for accounts
// that hold no access grant yet (the request IS how they get one).
export default function OrgStart({ onCreated, compact = false, referral = null }) {
  const { user } = useAuth();
  const email = user?.email || '';
  const [disc, setDisc] = useState(undefined); // undefined = loading
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [owned, setOwned] = useState(null); // 409 domain-owned from POST /orgs
  const requests = useMyJoinRequests();

  useEffect(() => {
    let alive = true;
    if (!email) { setDisc(null); return undefined; }
    discoverSignup(email).then((d) => alive && setDisc(d)).catch(() => alive && setDisc(null));
    return () => { alive = false; };
  }, [email]);

  const target = owned || disc?.org || null;
  const mine = target && (requests || []).find((r) => r.orgId === target.id);
  const canCreate = !target || target.allowOtherOrgs === true;

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true); setError('');
    try {
      const id = await createOrg({ name: name.trim() });
      if (referral) await referral(id);
      setName('');
      if (onCreated) onCreated(id);
    } catch (err) {
      if (err.code === 'domain-owned' && err.org) setOwned(err.org);
      else setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const request = async () => {
    if (!target || busy) return;
    setBusy(true); setError('');
    try { await createJoinRequest(target, { note }); setNote(''); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  if (disc === undefined) return <p className="cs-help" style={{ marginTop: 16 }}>Checking {disc?.domain || email.split('@')[1] || 'your email'}…</p>;

  return (
    <div style={{ marginTop: 16, maxWidth: 560 }}>
      {target && (
        <div className="cs-card" style={{ padding: 14 }}>
          <strong style={{ color: 'var(--text)' }}>{target.name} already uses nventr</strong>
          <p className="cs-help" style={{ marginTop: 6 }}>
            Your email is on <code>{disc?.domain || email.split('@')[1]}</code>, a domain {target.name} has verified.
            {mine?.status === 'pending' && ' Your request is with their administrators — you will get an email when they decide.'}
            {mine?.status === 'declined' && ' An administrator declined your request. Ask them for an invite from Members if you think that is a mistake.'}
            {mine?.status === 'approved' && ' Approved — seating you now…'}
            {mine?.status === 'done' && ' You are a member.'}
            {!mine && target.joinPolicy === 'off' && ' Joining is by invitation only: ask an administrator to invite you from Members.'}
            {!mine && target.joinPolicy !== 'off' && ' Ask to join and an administrator will approve you.'}
          </p>
          {!mine && target.joinPolicy !== 'off' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <input className="cs-input" style={{ flex: 1, minWidth: 200 }} placeholder="Optional note for the administrators (team, role…)" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
              <button type="button" className="cs-btn cs-btn-primary" disabled={busy} onClick={request}>{busy ? 'Sending…' : `Request to join ${target.name}`}</button>
            </div>
          )}
          {mine && ['pending', 'declined'].includes(mine.status) && (
            <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" style={{ marginTop: 8 }} onClick={() => withdrawJoinRequest(mine.id).catch((e) => setError(e.message))}>
              {mine.status === 'pending' ? 'Withdraw request' : 'Dismiss'}
            </button>
          )}
        </div>
      )}

      {canCreate && (
        <form style={{ display: 'flex', gap: 8, marginTop: target ? 14 : 0, flexWrap: 'wrap' }} onSubmit={create}>
          <input
            className="cs-input"
            style={{ flex: 1, minWidth: 220 }}
            placeholder={target ? 'Or create a separate organization…' : 'New organization name…'}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
          />
          <button className="cs-btn cs-btn-primary" disabled={!name.trim() || busy} type="submit">Create</button>
        </form>
      )}
      {canCreate && !target && disc && !disc.consumer && !compact && (
        <p className="cs-help" style={{ marginTop: 8, fontSize: 12 }}>
          You will own this organization. Verify <code>{disc.domain}</code> under Administration → Identity → Domains afterwards so colleagues who sign up find it instead of creating their own, and so you can transfer ownership to the right person.
        </p>
      )}
      {error && <p className="cs-help" style={{ color: 'var(--danger, #e5484d)', marginTop: 8 }}>{error}</p>}
    </div>
  );
}
